import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaPhoneAlt,
  FaLock,
  FaHeart,
  FaSadTear,
  FaCheckCircle,
} from "react-icons/fa";
import api from "../../api/axios";
import "../../css/User/DeleteAccountFlow.css";

export default function DeleteAccountFlow({ onClose }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    mobileNumber: "",
    password: "",
    reason: "",
  });
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const reasonOptions = [
    { value: "privacy", label: "Privacy concerns" },
    { value: "notifications", label: "Too many notifications" },
    { value: "not_useful", label: "Not useful for me" },
    { value: "technical", label: "Technical issues" },
    { value: "alternative", label: "Found a better alternative" },
    { value: "inactive", label: "Account is inactive" },
    { value: "other", label: "Other" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobileNumber") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const validateStep1 = () => {
    const mobileRegex = /^[0-9]{10}$/;
    if (!formData.mobileNumber) {
      setError("Please enter your mobile number");
      return false;
    }
    if (!mobileRegex.test(formData.mobileNumber)) {
      setError("Please enter a valid 10-digit mobile number");
      return false;
    }
    if (!formData.password) {
      setError("Please enter your password");
      return false;
    }
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return false;
    }
    if (!formData.reason) {
      setError("Please select a reason for deletion");
      return false;
    }
    return true;
  };

  const handleContinue = () => {
    if (validateStep1()) {
      setStep(2);
      setError("");
    }
  };

  const handleBack = () => {
    setStep(1);
    setConfirmDeletion(false);
    setError("");
  };

  const handleSubmit = async () => {
    if (!confirmDeletion) {
      setError("Please confirm that you want to delete your account");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const deleteReason = reasonOptions.find(
        (opt) => opt.value === formData.reason,
      )?.label;

      //   const requestData = {
      //     mobileNumber: formData.mobileNumber,
      //     password: formData.password,
      //     reason: deleteReason,
      //     requestedAt: new Date().toISOString(),
      //     deletionDate: new Date(
      //       Date.now() + 30 * 24 * 60 * 60 * 1000,
      //     ).toISOString(),
      //     gracePeriodDays: 30,
      //   };

      const requestData = {
        phone: formData.mobileNumber, // match backend
        password: formData.password,
        reason: deleteReason,
        consent: confirmDeletion, // REQUIRED
      };

      const response = await api.post("/user/delete-account", requestData);
      if (response.data.success) {
        onClose();

        navigate("/deletion-request-success", {
          state: {
            requestId: "REQ-" + Date.now(),
            mobileNumber: formData.mobileNumber,
            deletionDate: response.data.deletionDate,
            processingDays: 30,
          },
        });
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Invalid password. Please try again.");
      } else if (err.response?.status === 404) {
        setError("No account found with this mobile number");
      } else {
        setError(
          err.response?.data?.message || "Failed to submit deletion request",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="delete-flow-overlay" onClick={onClose}>
      <div
        className="delete-flow-container"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="delete-flow-close" onClick={onClose}>
          ×
        </button>

        {step === 1 ? (
          // Step 1: Form Page
          <div className="delete-flow-step">
            <div className="delete-flow-header">
              {/* <div className="delete-flow-icon">
                <FaSadTear />
              </div> */}
              <h2>Delete Account</h2>
              <p>Please verify your details to continue</p>
            </div>

            <div className="delete-flow-body">
              <div className="delete-form-group">
                <label>
                  <FaPhoneAlt className="input-icon" />
                  Mobile Number <span>*</span>
                </label>
                <input
                  type="text"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  placeholder="Enter your registered mobile number"
                  maxLength={10}
                  autoComplete="tel"
                />
                <small>Enter the mobile number linked to your account</small>
              </div>

              <div className="delete-form-group">
                <label>
                  <FaLock className="input-icon" />
                  Password <span>*</span>
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your account password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                <small>Enter your password to verify account ownership</small>
              </div>

              <div className="delete-form-group">
                <label>
                  Reason for Deletion <span>*</span>
                </label>
                <select
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                >
                  <option value="">Select a reason</option>
                  {reasonOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {error && <div className="delete-error-message">{error}</div>}
            </div>

            <div className="delete-flow-footer">
              <button className="delete-cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button className="delete-continue-btn" onClick={handleContinue}>
                Continue
              </button>
            </div>
          </div>
        ) : (
          // Step 2: Confirmation Page
          <div className="delete-flow-step">
            <div className="delete-flow-header">
              {/* <div className="delete-flow-icon farewell-icon">
                <FaHeart />
              </div> */}
              <h2>We're Sad to See You Go</h2>
            </div>

            <div className="delete-flow-body confirmation-body">
              <div className="farewell-message">
                <p>
                  We're truly sorry to hear you'd like to leave. Your experience
                  matters to us, and we completely understand your decision.
                </p>

                <p>
                  Before you go, we want to make sure this is the right choice
                  for you. Deleting your account means all your data will be
                  permanently removed and cannot be recovered.
                </p>

                <p>
                  If there's anything we could have done better, we'd love to
                  hear your feedback. But if you're ready to proceed, we respect
                  that — simply confirm below and your account will be scheduled
                  for deletion.
                </p>

                <p className="thankyou-message">
                  Thank you for being with us. We wish you all the best.
                  <span className="restore-message">
                    If you login again within 30 days, your account will be
                    restored.
                  </span>
                </p>
              </div>

              <div className="confirmation-checkbox">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={confirmDeletion}
                    onChange={(e) => setConfirmDeletion(e.target.checked)}
                  />
                  <span>
                    I understand that this action is{" "}
                    <strong>irreversible</strong> and all my data will be
                    permanently deleted after 30 days
                  </span>
                </label>
              </div>

              {error && <div className="delete-error-message">{error}</div>}
            </div>

            <div className="delete-flow-footer">
              <button className="delete-back-btn" onClick={handleBack}>
                Back
              </button>
              <button
                className="delete-submit-btn"
                onClick={handleSubmit}
                disabled={!confirmDeletion || isLoading}
              >
                {isLoading ? "Processing..." : "Delete My Account"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
